import React, { useMemo } from 'react';
import { Tooltip, AreaChart, ResponsiveContainer, CartesianGrid, XAxis, Area } from 'recharts';

import { Card, CardContent, Grid, Typography, Button } from '@material-ui/core';

import { makeStyles, Theme } from '@material-ui/core/styles';

import Backdrop from '@material-ui/core/Backdrop';
import { COLOR_E, COLOR_TOOLTIP_BACKGROUND } from 'src/const';
import { AreaChartRatioCardPropTypes } from 'src/interfaces/iproptypes';
import { AreaChartGraphObject, ParsedAreaChartValues } from 'src/interfaces/idashboard';
import { InfoTooltip } from '../Tooltips';
import { IconButton } from '@mui/material';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { infoIconButton, infoIconStyle } from 'src/App/Dashboards/Market/styles/style';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flex: 1,
    maxHeight: 295,
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
    [theme.breakpoints.up('sm')]: {
      maxHeight: 290,
    },
  },
  successText: {
    color: theme.palette.success.main,
    fontWeight: 900,
  },
  errorText: {
    color: theme.palette.error.main,
    fontWeight: 900,
  },
  subtleText: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  rateText: {
    fontWeight: 900,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 100,
  },
  sampleText: {
    color: COLOR_E,
    fontWeight: 600,
    transform: 'rotate(-30deg)',
    msTransform: 'rotate(-30deg)' /* IE 9 */,
    webkitTransform: 'rotate(-30deg),' /* Opera, Chrome, and Safari */,
  },
}));

const months = {
  0: 'Jan',
  1: 'Feb',
  2: 'Mar',
  3: 'Apr',
  4: 'May',
  5: 'Jun',
  6: 'Jul',
  7: 'Aug',
  8: 'Sept',
  9: 'Oct',
  10: 'Nov',
  11: 'Dec',
};

const isEmpty = (obj: string): boolean => {
  return !(Object.keys(obj).length > 0);
};

const parseGraphVal = (values: string): AreaChartGraphObject[] => {
  if (!values || isEmpty(values) || values == '{}') {
    return [
      {
        name: '2020-01-01',
        domestic: 0,
        international: 0,
      },
    ];
  }
  let valuesObj: ParsedAreaChartValues = JSON.parse(values);
  let graphObj: AreaChartGraphObject[] = [];
  let domestic = valuesObj['domestic'];
  let international = valuesObj['international'];
  let common_obj = Object.keys(domestic).length !== 0 ? domestic : international;
  for (var date in common_obj) {
    let objToInsert = {
      name: date,
      domestic: domestic ? domestic[date] : 0,
      international: international ? international[date] : 0,
    };
    graphObj.push(objToInsert);
  }
  return graphObj;
};
/*
{2018: {…}, 2019: {…}, 2020: {…}}
[{"name": "1", "2018":22, "2019":33}, ]
2018: {1: 24.9, 2: 27.2, 3: 33.5, 4: 42.1, 5: 49.1, 6: 55.2, 7: 53.2, 8: 52.7, 9: 50.2, 10: 48.6, 11: 43.3, 12: 34.2, total: 207}
2019: {1: 30.2, 2: 33.2, 3: 38.5, 4: 45.7, 5: 51.7, 6: 57.8, 7: 55.9, 8: 50.5, 9: 45.5, 10: 42.5, 11: 38.8, 12: 38.5, total: 319}
2020: {1: 41.5, 2: 40.9, 3: 27.9, 4: 13.6, 5: 3.5, total: 348}
*/

const animationTime = 120;

const CustomTooltip = ({ active, payload, label, classes }) => {
  if (active) {
    return (
      <Card>
        <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
          <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
            {payload[0].payload.name}
          </Typography>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            Domestic:{' '}
            {typeof payload[0].payload.domestic === 'undefined'
              ? 'N/A'
              : `${payload[0].payload.domestic}`}
            <br />
            International:{' '}
            {typeof payload[0].payload.international === 'undefined'
              ? 'N/A'
              : `${payload[0].payload.international}`}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return null;
};

const AreaChartRatioCard = ({
  title,
  data,
  date,
  tooltipContent,
  isDisabled,
  handleOnSampleClick,
}: AreaChartRatioCardPropTypes) => {
  const classes = useStyles();

  const [graphData] = useMemo(() => {
    return [parseGraphVal(data)];
  }, [data]);

  let noPropertyAvail: boolean = false;
  if (graphData.length === 0) {
    noPropertyAvail = true;
  }

  let blurGraph = {};
  // if (isDisabled) {
  //   blurGraph = {
  //     webkitFilter: 'blur(2px)',
  //   };
  // }

  return (
    <Card className={classes.root}>
      <CardContent>
        <Grid container>
          <Grid item xs={6}>
            <Typography variant="body1" className="subHeadings">
              {title}
              {tooltipContent && (
                <InfoTooltip
                  title={
                    <Card>
                      <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                        <Grid container direction="column" alignItems="flex-start">
                          <Typography
                            variant="body1"
                            style={{ fontWeight: 700, marginBottom: '20px' }}
                          >
                            {title}
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {tooltipContent}
                          </Typography>
                        </Grid>
                      </CardContent>
                    </Card>
                  }
                  enterTouchDelay={0}
                  leaveTouchDelay={10000}
                >
                  <IconButton style={infoIconButton}>
                    <InfoOutlinedIcon style={infoIconStyle} />
                  </IconButton>
                </InfoTooltip>
              )}
            </Typography>

            <Typography className={classes.subtleText}>{date}</Typography>
          </Grid>

          <Grid item container xs={12} justify="flex-end">
            <Grid container item xs={12} style={{ position: 'relative', top: '0', left: '0' ,}}>
              <ResponsiveContainer height={180} width="100%">
                {noPropertyAvail ? (
                  <Typography color="textSecondary">
                    Not enough data matching this criteria.{' '}
                  </Typography>
                ) : (
                  <AreaChart
                    data={graphData}
                    margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
                    style={blurGraph}
                    sx={{pointerEvents: isDisabled ? 'none' : 'auto'}}
                  >
                    <defs>
                      <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#758afe" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#758afe" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#58C0A9" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#58C0A9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="name" hide={true} />

                    <CartesianGrid strokeDasharray="3 3" />
                    <Tooltip content={<CustomTooltip classes={classes} active payload label />} />

                    <Area
                      type="monotone"
                      dataKey="domestic"
                      stroke="#758afe"
                      fillOpacity={1}
                      fill="url(#colorUv)"
                      isAnimationActive={true}
                      animationDuration={animationTime}
                    />
                    <Area
                      type="monotone"
                      dataKey="international"
                      stroke="#58C0A9"
                      fillOpacity={1}
                      fill="url(#colorPv)"
                      isAnimationActive={true}
                      animationDuration={animationTime}
                    />
                  </AreaChart>
                )}
              </ResponsiveContainer>

              {isDisabled && (
                <div
                  style={{
                    height: '100%',
                    position: 'absolute',
                    top: '0',
                    left: '0px',
                    width: '100%',
                    background: '#ffffff',
                    filter: 'blur( 6px)',
                    opacity: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                ></div>
              )}
              {isDisabled && (
                <Typography
                  style={{
                    position: 'absolute',
                    top: '40%',
                    left: '50%',
                    fontSize: '26px',
                    fontWeight: 900,
                    color: COLOR_E,
                    transform: 'translate(-50%, -50%)',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    handleOnSampleClick();
                  }}
                >
                  PREMIUM
                </Typography>
              )}
            </Grid>

            <Grid item container xs={12} justify="flex-end" alignItems="center"></Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AreaChartRatioCard;
